import React, { useCallback, useRef, useState } from 'react'
import {
  Box,
  Flex,
  VStack,
  Text,
  Button,
  Divider,
  Icon,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react'
import {
  FiFolder,
  FiUpload,
  FiCpu,
  FiEye,
  FiChevronRight,
  FiSearch,
  FiShare2,
  FiStar,
  FiTrash2,
  FiMessageSquare,
} from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'

const SECTIONS = [
  { key: 'folders', icon: FiFolder, stepCount: 4 },
  { key: 'uploading', icon: FiUpload, stepCount: 5 },
  { key: 'extraction', icon: FiCpu, stepCount: 4 },
  { key: 'viewing', icon: FiEye, stepCount: 5 },
  { key: 'searching', icon: FiSearch, stepCount: 5 },
  { key: 'sharing', icon: FiShare2, stepCount: 5 },
  { key: 'favorites', icon: FiStar, stepCount: 3 },
  { key: 'trash', icon: FiTrash2, stepCount: 4 },
  { key: 'aiAssistant', icon: FiMessageSquare, stepCount: 4 },
] as const

type SectionKey = (typeof SECTIONS)[number]['key']

export default function HelpPage() {
  const { t } = useAppTranslation('common')
  const [activeSection, setActiveSection] = useState<SectionKey | null>(null)
  const sectionRefs = useRef<Record<SectionKey, HTMLDivElement | null>>({
    folders: null,
    uploading: null,
    extraction: null,
    viewing: null,
    searching: null,
    sharing: null,
    favorites: null,
    trash: null,
    aiAssistant: null,
  })

  const scrollToSection = useCallback((key: SectionKey) => {
    const el = sectionRefs.current[key]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveSection(key)
    }
  }, [])

  const sections = SECTIONS.map(({ key, icon, stepCount }) => ({
    key,
    icon,
    title: t(`help.sections.${key}.title`),
    steps: Array.from({ length: stepCount }, (_, i) => t(`help.sections.${key}.steps.${i}`)),
  }))

  return (
    <Box h="full" p={6} overflow="auto">
      <VStack spacing={1} align="start" mb={8}>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800">
          {t('help.pageTitle')}
        </Text>
        <Text fontSize="md" color="gray.500">
          {t('help.pageSubtitle')}
        </Text>
      </VStack>

      <Flex gap={8} align="start">
        <Box flex={1} minW={0}>
          <VStack spacing={0} align="stretch">
            {sections.map((section, idx) => (
              <Box
                key={section.key}
                ref={(el) => {
                  sectionRefs.current[section.key] = el
                }}
                id={`help-${section.key}`}
                scrollMarginTop="80px"
              >
                <Flex align="center" gap={3} mb={3}>
                  <Flex
                    align="center"
                    justify="center"
                    w="36px"
                    h="36px"
                    borderRadius="lg"
                    bg="brand.50"
                    flexShrink={0}
                  >
                    <Icon as={section.icon} boxSize={5} color="brand.600" />
                  </Flex>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                    {section.title}
                  </Text>
                </Flex>

                <List spacing={2} pl="48px" mb={2}>
                  {section.steps.map((step, stepIdx) => (
                    <ListItem
                      key={stepIdx}
                      fontSize="sm"
                      color="gray.600"
                      lineHeight="tall"
                      display="flex"
                      alignItems="flex-start"
                    >
                      <ListIcon
                        as={FiChevronRight}
                        color="brand.400"
                        mt="5px"
                        flexShrink={0}
                      />
                      {step}
                    </ListItem>
                  ))}
                </List>

                {idx < sections.length - 1 && <Divider my={6} />}
              </Box>
            ))}
          </VStack>
        </Box>

        <Box
          position="sticky"
          top="80px"
          w="260px"
          flexShrink={0}
          display={{ base: 'none', lg: 'block' }}
        >
          <Box
            bg="white"
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            p={4}
          >
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              color="gray.400"
              letterSpacing="wider"
              mb={3}
            >
              {t('help.sidebar.title')}
            </Text>
            <VStack align="stretch" spacing={1}>
              {sections.map((section) => {
                const isActive = activeSection === section.key
                return (
                  <Button
                    key={section.key}
                    variant="ghost"
                    size="sm"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={section.icon} boxSize={4} />}
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    color={isActive ? 'brand.600' : 'gray.600'}
                    bg={isActive ? 'brand.50' : 'transparent'}
                    _hover={{
                      bg: isActive ? 'brand.50' : 'gray.100',
                      color: isActive ? 'brand.600' : 'gray.800',
                    }}
                    borderRadius="lg"
                    h="36px"
                    onClick={() => scrollToSection(section.key)}
                  >
                    <Text fontSize="sm" noOfLines={1}>
                      {section.title}
                    </Text>
                  </Button>
                )
              })}
            </VStack>
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}
